import { alpha, Box, FormControl, styled, Theme } from '@mui/material';
import { SxProps } from '@mui/material/styles';

export const StyledDialogHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
}));

export const dialogPaperSx = {
  height: '90vh',
  position: 'relative',
} as const;

export const dialogTitleSx = {
  textTransform: 'uppercase',
  fontWeight: 'bold',
} as const;

export const closeButtonSx = {
  mr: -2,
  mt: -1,
} as const;

export const dialogContentSx = {
  p: 0,
  display: 'flex',
  flexDirection: 'column',
} as const;

export const FilterSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.01),
}));

interface StyledFilterControlProps {
  isDisabled?: boolean;
}

export const StyledFilterControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<StyledFilterControlProps>(({ theme, isDisabled }) => ({
  transition: theme.transitions.create(['opacity', 'background-color'], {
    duration: theme.transitions.duration.short,
  }),
  opacity: isDisabled ? 0.5 : 1,

  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : theme.palette.background.paper,

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.04),
    },
  },
}));

export const searchSectionSx: SxProps<Theme> = {
  px: 3,
  py: 2,
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
};

export const resultsContainerSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  position: 'relative',
};

export const dataGridSx: SxProps<Theme> = {
  height: '100%',
  border: 'none',
  bgcolor: 'transparent !important',
  '& .MuiDataGrid-scrollbar': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    marginBottom: '4px',
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : alpha(theme.palette.common.black, 0.04),
  },
  '& .datapoint-row.MuiDataGrid-row': {
    borderBottom: (theme: Theme) =>
      `solid 1px ${theme.palette.mode === 'dark' ? 'rgb(80 85 89)' : '#e0e0e0'}`,
  },
  '& .datapoint-row .MuiDataGrid-cell': {
    border: 'none',
  },
};

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

export const ActiveSearchIndicator = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.info.main, 0.15)
      : alpha(theme.palette.info.main, 0.1),
  color: theme.palette.info.main,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.75rem',
}));

export const DropdownLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  borderRadius: theme.shape.borderRadius,
  zIndex: 1,
}));

export const searchFormSx = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
} as const;

export const searchButtonSx = {
  minWidth: 'auto',
  px: 2,
} as const;

export const emptyStateIconSx = {
  fontSize: 48,
  mb: 2,
  opacity: 0.5,
} as const;
